'use client';
import { useState } from 'react';
import Link from 'next/link';
import { industries } from '@/lib/seo-database';
interface QuizAnswer {
  questionIndex: number;
  value: string | number;
  score: number;
}
const QUESTIONS = [
  {
    question: 'What industry is your business in?',
    type: 'select' as const,
    options: industries.map((i) => ({ label: i.name, value: i.slug })),
    scoreMap: {},  // no score — just context
  },
  {
    question: 'How many people work in your business?',
    type: 'select' as const,
    options: [
      { label: 'Just me (sole trader)', value: '1', score: 5 },
      { label: '2-10 employees', value: '2-10', score: 10 },
      { label: '11-50 employees', value: '11-50', score: 12 },
      { label: '51-200 employees', value: '51-200', score: 10 },
      { label: '200+ employees', value: '200+', score: 8 },
    ],
  },
  {
    question: 'How many customer enquiries do you receive per week?',
    type: 'select' as const,
    options: [
      { label: 'Fewer than 10', value: '<10', score: 4 },
      { label: '10-50', value: '10-50', score: 8 },
      { label: '50-200', value: '50-200', score: 12 },
      { label: '200-500', value: '200-500', score: 14 },
      { label: 'More than 500', value: '500+', score: 12 },
    ],
  },
  {
    question: 'What is your biggest operational pain point?',
    type: 'select' as const,
    options: [
      { label: 'Missed calls and slow responses', value: 'missed-calls', score: 14 },
      { label: 'Too much time on admin and repetitive tasks', value: 'admin', score: 12 },
      { label: 'Difficulty managing bookings', value: 'bookings', score: 12 },
      { label: 'Not enough leads or follow-up', value: 'leads', score: 10 },
      { label: 'Managing online reviews', value: 'reviews', score: 8 },
      { label: 'No major pain point currently', value: 'none', score: 2 },
    ],
  },
  {
    question: 'How do you currently handle customer enquiries outside business hours?',
    type: 'select' as const,
    options: [
      { label: 'We don\'t — they wait until we open', value: 'nothing', score: 14 },
      { label: 'Voicemail or contact form', value: 'voicemail', score: 10 },
      { label: 'Basic auto-reply email', value: 'auto-reply', score: 8 },
      { label: 'We have staff covering extended hours', value: 'staff', score: 6 },
      { label: 'We already use a chatbot or AI tool', value: 'ai', score: 4 },
    ],
  },
  {
    question: 'How many hours per week does your team spend on repetitive admin?',
    type: 'select' as const,
    options: [
      { label: 'Less than 5 hours', value: '<5', score: 4 },
      { label: '5-15 hours', value: '5-15', score: 8 },
      { label: '15-30 hours', value: '15-30', score: 12 },
      { label: 'More than 30 hours', value: '30+', score: 14 },
    ],
  },
  {
    question: 'Does your business have a website?',
    type: 'select' as const,
    options: [
      { label: 'Yes, with live chat', value: 'chat', score: 12 },
      { label: 'Yes, with a contact form', value: 'form', score: 10 },
      { label: 'Yes, but basic', value: 'basic', score: 8 },
      { label: 'No website', value: 'none', score: 2 },
    ],
  },
  {
    question: 'What is your monthly budget for business tools and software?',
    type: 'select' as const,
    options: [
      { label: 'Under $50/month', value: '<50', score: 4 },
      { label: '$50-$200/month', value: '50-200', score: 10 },
      { label: '$200-$500/month', value: '200-500', score: 12 },
      { label: 'Over $500/month', value: '500+', score: 10 },
    ],
  },
  {
    question: 'How comfortable are you with new technology?',
    type: 'select' as const,
    options: [
      { label: 'Very comfortable — I adopt early', value: 'early', score: 12 },
      { label: 'Fairly comfortable — I learn as I go', value: 'moderate', score: 10 },
      { label: 'A bit cautious — I need guidance', value: 'cautious', score: 6 },
      { label: 'Not comfortable — I prefer traditional methods', value: 'traditional', score: 2 },
    ],
  },
  {
    question: 'When would you ideally want to implement AI?',
    type: 'select' as const,
    options: [
      { label: 'As soon as possible', value: 'asap', score: 12 },
      { label: 'Within the next 3 months', value: '3months', score: 10 },
      { label: 'Within the next 6-12 months', value: '6-12months', score: 6 },
      { label: 'Just exploring for now', value: 'exploring', score: 4 },
    ],
  },
];
function getReadinessLevel(score: number) {
  if (score >= 90) return { level: 'Excellent', color: '#22c55e', message: 'Your business is highly ready for AI agents. You have the volume, the pain points, and the infrastructure to see significant benefits.' };
  if (score >= 70) return { level: 'Strong', color: '#4361ee', message: 'Your business is well-positioned for AI agents. A few areas could be strengthened, but the core readiness is there.' };
  if (score >= 50) return { level: 'Moderate', color: '#eab308', message: 'Your business could benefit from AI agents, but there are areas to address first. Consider starting with a single agent for your biggest pain point.' };
  if (score >= 30) return { level: 'Early Stage', color: '#f97316', message: 'AI agents may be premature for your current setup. Focus on building your digital presence and customer query volume first. That is honest advice, not a sales pitch.' };
  return { level: 'Not Yet', color: '#ef4444', message: 'Based on your responses, AI agents may not be the right investment right now. Focus on the fundamentals first — and that is perfectly fine. Not every business needs AI today.' };
}
export function ReadinessQuizClient() {
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswer[]>([]);
  const [completed, setCompleted] = useState(false);
  const handleAnswer = (value: string | number, score: number) => {
    const newAnswers = [...answers, { questionIndex: currentQ, value, score }];
    setAnswers(newAnswers);
    if (currentQ < QUESTIONS.length - 1) {
      setCurrentQ(currentQ + 1);
    } else {
      setCompleted(true);
    }
  };
  const totalScore = answers.reduce((sum, a) => sum + a.score, 0);
  const maxPossible = 126; // sum of max scores per question
  const percentage = Math.round((totalScore / maxPossible) * 100);
  const readiness = getReadinessLevel(percentage);
  const selectedIndustry = answers.find((a) => a.questionIndex === 0)?.value as string;
  if (completed) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20">
        <h1 className="text-3xl font-bold text-white font-[family-name:var(--font-outfit)] mb-8">
          Your AI Readiness Results
        </h1>
        {/* Score */}
        <div className="p-8 rounded-xl bg-white/[0.03] border border-white/[0.06] text-center mb-8">
          <div className="relative w-32 h-32 mx-auto mb-4">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
              <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
              <circle
                cx="50" cy="50" r="45" fill="none"
                stroke={readiness.color} strokeWidth="8"
                strokeDasharray={`${percentage * 2.83} ${283 - percentage * 2.83}`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-3xl font-bold text-white font-[family-name:var(--font-outfit)]">
                {percentage}
              </span>
            </div>
          </div>
          <p className="text-2xl font-semibold font-[family-name:var(--font-outfit)]" style={{ color: readiness.color }}>
            {readiness.level} Readiness
          </p>
          <p className="text-gray-400 mt-3 leading-relaxed max-w-lg mx-auto">
            {readiness.message}
          </p>
        </div>
        {/* Honest recommendation */}
        {percentage < 50 && (
          <div className="p-4 rounded-lg bg-amber-500/[0.05] border border-amber-500/[0.15] mb-8">
            <p className="text-amber-200/70 text-sm leading-relaxed">
              <strong className="text-amber-300">Our honest advice:</strong> We could try to
              sell you an AI agent anyway, but that would not serve you well. Focus on building
              the foundations first. When you are ready, we will be here.
            </p>
          </div>
        )}
        {/* Next steps */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-white font-[family-name:var(--font-outfit)]">
            Recommended Next Steps
          </h2>
          {percentage >= 50 && (
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/tools/roi-calculator"
                className="flex-1 p-4 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:border-[#4361ee]/30 transition-colors text-center"
              >
                <p className="text-white font-medium">📊 Calculate Your ROI</p>
                <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">See estimated savings for your business</p>
              </Link>
              {selectedIndustry && (
                <Link
                  href={`/guides/${selectedIndustry}`}
                  className="flex-1 p-4 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:border-[#4361ee]/30 transition-colors text-center"
                >
                  <p className="text-white font-medium">📖 Read Your Industry Guide</p>
                  <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">Detailed, honest analysis</p>
                </Link>
              )}
              <Link
                href="/tools/ai-agent-finder"
                className="flex-1 p-4 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:border-[#4361ee]/30 transition-colors text-center"
              >
                <p className="text-white font-medium">🔍 Find Your Agent</p>
                <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">Get matched with the right AI agent</p>
              </Link>
            </div>
          )}
          {percentage < 50 && (
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/glossary"
                className="flex-1 p-4 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:border-[#4361ee]/30 transition-colors text-center"
              >
                <p className="text-white font-medium">📚 Learn the Basics</p>
                <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">Our jargon-free AI glossary</p>
              </Link>
              <Link
                href="/resources/uk-small-business-ai-report"
                className="flex-1 p-4 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:border-[#4361ee]/30 transition-colors text-center"
              >
                <p className="text-white font-medium">📊 UK AI Report</p>
                <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">See where your industry stands</p>
              </Link>
            </div>
          )}
        </div>
        {/* Retake */}
        <div className="mt-8 text-center">
          <button
            onClick={() => { setCurrentQ(0); setAnswers([]); setCompleted(false); }}
            className="text-[#4361ee] hover:underline text-sm"
          >
            Retake quiz
          </button>
        </div>
      </div>
    );
  }
  // Quiz in progress
  const q = QUESTIONS[currentQ];
  return (
    <div className="max-w-2xl mx-auto px-6 py-20">
      <nav className="text-sm text-gray-500 dark:text-gray-400 mb-8" aria-label="Breadcrumb">
        <Link href="/" className="hover:text-white transition-colors">Home</Link>
        <span className="mx-2">/</span>
        <Link href="/tools" className="hover:text-white transition-colors">Tools</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-300">AI Readiness Quiz</span>
      </nav>
      <h1 className="text-3xl font-bold text-white font-[family-name:var(--font-outfit)] mb-2">
        AI Readiness Quiz
      </h1>
      <p className="text-gray-400 mb-8">
        10 questions. Instant results. No email required.
      </p>
      {/* Progress */}
      <div className="mb-8">
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
          <span>Question {currentQ + 1} of {QUESTIONS.length}</span>
          <span>{Math.round(((currentQ) / QUESTIONS.length) * 100)}%</span>
        </div>
        <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#4361ee] to-[#7c3aed] rounded-full transition-all duration-300"
            style={{ width: `${(currentQ / QUESTIONS.length) * 100}%` }}
          />
        </div>
      </div>
      {/* Question */}
      <h2 className="text-xl text-white font-medium font-[family-name:var(--font-outfit)] mb-6">
        {q.question}
      </h2>
      <div className="space-y-3">
        {q.options.map((opt) => {
          const score = 'score' in opt ? (opt as { score: number }).score : 0;
          return (
            <button
              key={String(opt.value)}
              onClick={() => handleAnswer(opt.value, score)}
              className="w-full text-left p-4 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:border-[#4361ee]/40 hover:bg-white/[0.06] transition-all text-gray-300 hover:text-white"
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
